import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
} from 'recharts';
import { api, ApiError } from '@/lib/api';
import { useSession } from '@/lib/session';

// Mirrors HMHApps.Portal.FoundryUsageRow / FoundryUsageResponse (camelCase JSON).
interface UsageRow {
  day: string;
  provider: string;
  model: string;
  app: string;
  calls: number;
  successCalls: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  estCostUsd: number;
  estSavedUsd: number;
}
interface UsageResponse {
  days: number;
  rows: UsageRow[];
}

const WINDOWS = [7, 30, 90] as const;

// Stable color per provider; the rest cycle a palette.
const PROVIDER_COLORS: Record<string, string> = {
  'foundry-anthropic': '#0e7490', // Foundry = ocean cyan
  anthropic: '#d97706', // direct = amber
  openai: '#10a37f',
  unknown: '#94a3b8',
};
const PALETTE = ['#334b9b', '#0e7490', '#a16207', '#7c3aed', '#15803d', '#e11d48', '#0284c7', '#475569'];
const colorFor = (key: string, i: number) => PROVIDER_COLORS[key] ?? PALETTE[i % PALETTE.length];

const fmtInt = (n: number) => n.toLocaleString();
const fmtTok = (n: number) => new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(n);
const fmtUsd = (n: number) => '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDay = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', day: '2-digit', timeZone: 'UTC' });

export default function FoundryUsage() {
  const { data: session } = useSession();
  const [days, setDays] = useState<number>(30);
  const [providerFilter, setProviderFilter] = useState('all');
  const [modelFilter, setModelFilter] = useState('all');
  const [appFilter, setAppFilter] = useState('all');

  const query = useQuery({
    queryKey: ['foundry-usage', days],
    queryFn: () => api.get<UsageResponse>(`/api/foundry-usage?days=${days}`),
    retry: false,
    staleTime: 5 * 60_000,
  });

  const rows = useMemo(() => query.data?.rows ?? [], [query.data]);

  const providers = useMemo(() => Array.from(new Set(rows.map((r) => r.provider))).sort(), [rows]);
  const models = useMemo(() => Array.from(new Set(rows.map((r) => r.model))).sort(), [rows]);
  const appsList = useMemo(() => Array.from(new Set(rows.map((r) => r.app))).sort(), [rows]);

  const filtered = useMemo(
    () =>
      rows.filter(
        (r) =>
          (providerFilter === 'all' || r.provider === providerFilter) &&
          (modelFilter === 'all' || r.model === modelFilter) &&
          (appFilter === 'all' || r.app === appFilter),
      ),
    [rows, providerFilter, modelFilter, appFilter],
  );

  const totals = useMemo(() => sum(filtered), [filtered]);

  // Tokens per day, stacked by provider.
  const dailySeries = useMemo(() => {
    const byDay = new Map<string, Record<string, number | string>>();
    for (const r of filtered) {
      const key = r.day;
      const row = byDay.get(key) ?? { day: fmtDay(r.day) };
      row[r.provider] = ((row[r.provider] as number) ?? 0) + r.inputTokens + r.outputTokens;
      byDay.set(key, row);
    }
    return Array.from(byDay.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, v]) => v);
  }, [filtered]);

  const byModel = useMemo(() => groupAgg(filtered, (r) => r.model), [filtered]);
  const byApp = useMemo(() => groupAgg(filtered, (r) => r.app), [filtered]);
  const byProvider = useMemo(() => groupAgg(filtered, (r) => r.provider), [filtered]);

  // Drill-down table: collapse the day dimension, one row per provider+model+app.
  const tableRows = useMemo(() => {
    const m = new Map<string, UsageAgg & { provider: string; model: string; app: string }>();
    for (const r of filtered) {
      const key = `${r.provider}|${r.model}|${r.app}`;
      const e = m.get(key) ?? { provider: r.provider, model: r.model, app: r.app, ...emptyAgg() };
      addRow(e, r);
      m.set(key, e);
    }
    return Array.from(m.values()).sort((a, b) => b.estCostUsd - a.estCostUsd || b.calls - a.calls);
  }, [filtered]);

  // Prompt tokens per day split by cache treatment (uncached / served from cache / written to cache).
  const cacheSeries = useMemo(() => {
    const byDay = new Map<string, { day: string; uncached: number; cacheRead: number; cacheWrite: number }>();
    for (const r of filtered) {
      const e = byDay.get(r.day) ?? { day: fmtDay(r.day), uncached: 0, cacheRead: 0, cacheWrite: 0 };
      e.uncached += r.inputTokens;
      e.cacheRead += r.cacheReadTokens ?? 0;
      e.cacheWrite += r.cacheWriteTokens ?? 0;
      byDay.set(r.day, e);
    }
    return Array.from(byDay.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, v]) => v);
  }, [filtered]);

  // ---- States ----
  const err = query.error;
  if (err instanceof ApiError && err.status === 403) {
    return <Notice title="Administrators only" body="The Foundry usage dashboard is restricted to portal administrators." />;
  }
  if (err instanceof ApiError && err.status === 501) {
    return (
      <Notice
        title="Not configured yet"
        body="Set LOG_ANALYTICS_WORKSPACE_ID on the portal and grant its managed identity Monitoring Reader on the shared Log Analytics workspace."
      />
    );
  }

  return (
    <div className="stagger px-1">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="eyebrow">HMH Apps · Platform</span>
          <h1 className="mt-2 font-(family-name:--font-heading) text-2xl font-bold text-text md:text-3xl">Foundry Usage</h1>
          <p className="mt-1 text-sm text-muted">
            Azure AI Foundry token, call &amp; cost usage across every HMH app · drill down by model and app
          </p>
        </div>
        <div className="flex items-center gap-2">
          {WINDOWS.map((w) => (
            <button
              key={w}
              onClick={() => setDays(w)}
              className={
                'rounded-md border px-3 py-1.5 text-sm transition ' +
                (days === w ? 'border-accent bg-accent/10 text-accent' : 'border-border text-muted hover:text-text')
              }
            >
              {w}d
            </button>
          ))}
        </div>
      </header>

      {query.isLoading && <div className="p-10 text-center text-muted">Loading usage…</div>}

      {!query.isLoading && rows.length === 0 && (
        <Notice
          title="No usage yet"
          body={`No GenAI calls recorded in the last ${days} days. Once apps run, chat spans appear here within a few minutes.`}
        />
      )}

      {!query.isLoading && rows.length > 0 && (
        <>
          {/* Filters */}
          <div className="mb-5 flex flex-wrap gap-3">
            <FilterSelect label="Provider" value={providerFilter} onChange={setProviderFilter} options={providers} />
            <FilterSelect label="Model" value={modelFilter} onChange={setModelFilter} options={models} />
            <FilterSelect label="App" value={appFilter} onChange={setAppFilter} options={appsList} />
          </div>

          {/* Summary cards */}
          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
            <Card label="Calls" value={fmtInt(totals.calls)} sub={`${pct(totals.successCalls, totals.calls)} success`} />
            <Card label="Input tokens" value={fmtTok(totals.inputTokens)} sub="uncached" />
            <Card label="Output tokens" value={fmtTok(totals.outputTokens)} />
            <Card label="Cache reads" value={fmtTok(totals.cacheReadTokens)} sub={`writes ${fmtTok(totals.cacheWriteTokens)}`} />
            <Card label="Cache hit rate" value={cacheHitPct(totals)} sub="of prompt tokens" />
            <Card label="Est. cost" value={fmtUsd(totals.estCostUsd)} sub="estimated" />
            <Card label="Cache savings" value={fmtUsd(totals.estSavedUsd)} sub="vs no cache" />
            <Card label="Providers" value={String(byProvider.length)} sub={byProvider.map((p) => p.key).join(', ')} />
          </div>

          {/* Tokens over time, stacked by provider */}
          <Panel title="Tokens per day — by provider">
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={dailySeries} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="var(--muted)" />
                <YAxis tickFormatter={(v) => fmtTok(v as number)} tick={{ fontSize: 11 }} stroke="var(--muted)" width={48} />
                <Tooltip formatter={(v) => fmtInt(v as number)} contentStyle={tooltipStyle} />
                <Legend />
                {providers.map((p, i) => (
                  <Area
                    key={p}
                    type="monotone"
                    dataKey={p}
                    stackId="1"
                    stroke={colorFor(p, i)}
                    fill={colorFor(p, i)}
                    fillOpacity={0.5}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </Panel>

          {/* Prompt-cache effectiveness: cached vs uncached prompt tokens per day */}
          <Panel title="Prompt cache — prompt tokens per day" className="mt-4">
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={cacheSeries} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="var(--muted)" />
                <YAxis tickFormatter={(v) => fmtTok(v as number)} tick={{ fontSize: 11 }} stroke="var(--muted)" width={48} />
                <Tooltip formatter={(v) => fmtInt(v as number)} contentStyle={tooltipStyle} />
                <Legend />
                <Area type="monotone" dataKey="uncached" name="Uncached (1×)" stackId="1" stroke="#94a3b8" fill="#94a3b8" fillOpacity={0.5} />
                <Area type="monotone" dataKey="cacheWrite" name="Cache write (1.25×)" stackId="1" stroke="#a16207" fill="#a16207" fillOpacity={0.5} />
                <Area type="monotone" dataKey="cacheRead" name="Cache read (0.1×)" stackId="1" stroke="#15803d" fill="#15803d" fillOpacity={0.5} />
              </AreaChart>
            </ResponsiveContainer>
            <p className="mt-2 text-xs text-muted">
              Cache metrics require HMH.Platform.Ai ≥ 0.6.0 spans — older traffic shows as uncached.
            </p>
          </Panel>

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Panel title="Est. cost by model">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={byModel} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                  <XAxis type="number" tickFormatter={(v) => fmtUsd(v as number)} tick={{ fontSize: 11 }} stroke="var(--muted)" />
                  <YAxis type="category" dataKey="key" width={130} tick={{ fontSize: 11 }} stroke="var(--muted)" />
                  <Tooltip formatter={(v) => fmtUsd(v as number)} contentStyle={tooltipStyle} />
                  <Bar dataKey="estCostUsd" radius={[0, 4, 4, 0]}>
                    {byModel.map((d, i) => (
                      <Cell key={d.key} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="Calls by app">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={byApp} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                  <XAxis type="number" tickFormatter={(v) => fmtTok(v as number)} tick={{ fontSize: 11 }} stroke="var(--muted)" />
                  <YAxis type="category" dataKey="key" width={150} tick={{ fontSize: 11 }} stroke="var(--muted)" />
                  <Tooltip formatter={(v) => fmtInt(v as number)} contentStyle={tooltipStyle} />
                  <Bar dataKey="calls" radius={[0, 4, 4, 0]}>
                    {byApp.map((d, i) => (
                      <Cell key={d.key} fill={PALETTE[(i + 3) % PALETTE.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Panel>
          </div>

          {/* Drill-down table */}
          <Panel title="Drill-down — provider × model × app" className="mt-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
                    <th className="px-2 py-2">Provider</th>
                    <th className="px-2 py-2">Model</th>
                    <th className="px-2 py-2">App</th>
                    <th className="px-2 py-2 text-right">Calls</th>
                    <th className="px-2 py-2 text-right">Success</th>
                    <th className="px-2 py-2 text-right">In tok</th>
                    <th className="px-2 py-2 text-right">Out tok</th>
                    <th className="px-2 py-2 text-right">Cache read</th>
                    <th className="px-2 py-2 text-right">Hit %</th>
                    <th className="px-2 py-2 text-right">Est. cost</th>
                    <th className="px-2 py-2 text-right">Saved</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((r) => (
                    <tr key={`${r.provider}|${r.model}|${r.app}`} className="border-b border-border/60 hover:bg-surface">
                      <td className="px-2 py-1.5">
                        <span
                          className="inline-block h-2 w-2 rounded-full align-middle"
                          style={{ background: PROVIDER_COLORS[r.provider] ?? '#94a3b8' }}
                        />{' '}
                        {r.provider}
                      </td>
                      <td className="px-2 py-1.5 text-text">{r.model}</td>
                      <td className="px-2 py-1.5 text-muted">{r.app}</td>
                      <td className="px-2 py-1.5 text-right">{fmtInt(r.calls)}</td>
                      <td className="px-2 py-1.5 text-right">{pct(r.successCalls, r.calls)}</td>
                      <td className="px-2 py-1.5 text-right">{fmtTok(r.inputTokens)}</td>
                      <td className="px-2 py-1.5 text-right">{fmtTok(r.outputTokens)}</td>
                      <td className="px-2 py-1.5 text-right">{fmtTok(r.cacheReadTokens)}</td>
                      <td className="px-2 py-1.5 text-right">{cacheHitPct(r)}</td>
                      <td className="px-2 py-1.5 text-right font-medium text-text">{fmtUsd(r.estCostUsd)}</td>
                      <td className="px-2 py-1.5 text-right text-emerald-600">{fmtUsd(r.estSavedUsd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-muted">
              Cost is <em>estimated</em> from token counts × per-model list rates (cache reads 0.1×, cache writes 1.25× the
              input rate) — reconcile against the Azure cost export. “Saved” is net vs a no-cache world (reads save 0.9×,
              writes cost an extra 0.25×) and can be slightly negative on write-heavy days.
              {!session?.isAdmin && ' (admin view)'}
            </p>
          </Panel>
        </>
      )}
    </div>
  );
}

/* ---------- aggregation helpers ---------- */

interface UsageAgg {
  calls: number;
  successCalls: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  estCostUsd: number;
  estSavedUsd: number;
}

const emptyAgg = (): UsageAgg => ({
  calls: 0,
  successCalls: 0,
  inputTokens: 0,
  outputTokens: 0,
  cacheReadTokens: 0,
  cacheWriteTokens: 0,
  estCostUsd: 0,
  estSavedUsd: 0,
});

function addRow(a: UsageAgg, r: UsageRow | UsageAgg) {
  a.calls += r.calls;
  a.successCalls += r.successCalls;
  a.inputTokens += r.inputTokens;
  a.outputTokens += r.outputTokens;
  a.cacheReadTokens += r.cacheReadTokens ?? 0;
  a.cacheWriteTokens += r.cacheWriteTokens ?? 0;
  a.estCostUsd += r.estCostUsd;
  a.estSavedUsd += r.estSavedUsd ?? 0;
}

function sum(rows: UsageRow[]): UsageAgg {
  const a = emptyAgg();
  for (const r of rows) addRow(a, r);
  return a;
}

function groupAgg(rows: UsageRow[], keyFn: (r: UsageRow) => string): Array<UsageAgg & { key: string }> {
  const m = new Map<string, UsageAgg & { key: string }>();
  for (const r of rows) {
    const k = keyFn(r);
    const e = m.get(k) ?? { key: k, ...emptyAgg() };
    addRow(e, r);
    m.set(k, e);
  }
  return Array.from(m.values()).sort((a, b) => b.estCostUsd - a.estCostUsd || b.calls - a.calls);
}

/** Share of prompt tokens served from cache (reads / all prompt tokens). */
const cacheHitPct = (a: UsageAgg) => {
  const promptTok = a.inputTokens + a.cacheReadTokens + a.cacheWriteTokens;
  return promptTok === 0 ? '—' : `${Math.round((a.cacheReadTokens / promptTok) * 100)}%`;
};

const pct = (num: number, den: number) => (den === 0 ? '—' : `${Math.round((num / den) * 100)}%`);

const tooltipStyle = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  fontSize: 12,
} as const;

/* ---------- presentational pieces ---------- */

function Card({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface px-4 py-3">
      <div className="text-[0.7rem] uppercase tracking-[0.12em] text-muted">{label}</div>
      <div className="mt-1 text-xl font-bold text-text">{value}</div>
      {sub && <div className="mt-0.5 truncate text-xs text-muted">{sub}</div>}
    </div>
  );
}

function Panel({ title, children, className }: { title: string; children: React.ReactNode; className?: string }) {
  return (
    <section className={'rounded-lg border border-border bg-surface p-4 ' + (className ?? '')}>
      <h2 className="mb-3 text-sm font-semibold text-text">{title}</h2>
      {children}
    </section>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-muted">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-border bg-surface px-2 py-1 text-text"
      >
        <option value="all">All</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

function Notice({ title, body }: { title: string; body: string }) {
  return (
    <div className="mx-auto mt-10 max-w-md rounded-lg border border-border bg-surface p-6 text-center">
      <h2 className="text-lg font-semibold text-text">{title}</h2>
      <p className="mt-2 text-sm text-muted">{body}</p>
    </div>
  );
}
